// This eslint rule is disable because we cannot use serverLogger in this file (circular dependency issue)
/* eslint-disable no-console */
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ERROR_LOGS, TOKEN_NAME } from 'constants/index';
import decode, { JwtPayload } from 'jwt-decode';
import { serverLogger } from 'utils/serverLogs.utils';

/**
 * Logs out the current user.
 * @returns {void}
 */
const logoutUser = (): void => {
  window.location.href = '/auth/logout?reason=expired';
};

/**
 * Determines the origin of the log (client or server).
 * @returns {'client' | 'server'} The origin of the log.
 */
const getLogOrigin = (): 'client' | 'server' => (typeof window !== 'undefined' ? 'client' : 'server');

/**
 * Checks if the JWT token stored in localStorage is expired.
 * @returns {boolean} True if the JWT is expired, false otherwise.
 * @throws {Error} If the JWT does not have an 'exp' property or if 'exp' is not a number.
 */
const isJwtExpired = (): boolean => {
  try {
    const jwt = window.localStorage.getItem(TOKEN_NAME);
    const { exp } = decode<JwtPayload>(jwt);

    // Check if 'exp' is undefined
    if (typeof exp === 'undefined') {
      serverLogger.error(ERROR_LOGS.JWT_NO_EXP_PROP, {}, 'api-factory-interceptor', getLogOrigin());
      return true;
    }

    // Check if 'exp' is not a number
    if (typeof exp !== 'number') {
      serverLogger.error(ERROR_LOGS.JWT_EXP_NOT_NUMBER, { exp }, 'api-factory-interceptor', getLogOrigin());
      return true;
    }

    // Convert 'exp' from seconds to milliseconds and compare with current time
    const expireDate = exp * 1000;
    const currentDate = new Date().getTime();
    return currentDate > expireDate;
  } catch (error) {
    serverLogger.error(
      `Error checking JWT expiration: ${error.message || ''}`,
      { context: error },
      'api-factory-interceptor',
      typeof window !== 'undefined' ? 'client' : 'server'
    );

    return true; // Assume expired if there's an error
  }
};

/**
 * Creates an Axios instance with interceptors for JWT expiration and logout handling.
 * @param {ApiFactoryConfig} config Optional configuration for the Axios instance.
 * @returns {AxiosInstance} Configured Axios instance with request and response interceptors.
 */
const apiFactory = ({
  baseURL,
  headers = null,
  timeout = 9000,
  responseType = 'json',
  responseEncoding = 'utf8',
  ...rest
}: AxiosRequestConfig = {}): AxiosInstance => {
  // Create an Axios instance with the provided configuration
  const instance = axios.create({
    baseURL,
    timeout,
    headers,
    responseType,
    responseEncoding,
    ...rest,
  });

  // Add request and response interceptors to handle JWT expiration and logout
  instance.interceptors.request.use(
    (config) => {
      return config;
    },
    (err: AxiosError) => Promise.reject(err)
  );

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (err: AxiosError) => {
      // Check if the error is due to JWT expiration or unauthorized access
      if (err.response && err.response.status === 403) {
        const method = err.config?.method?.toUpperCase() || 'UNKNOWN_METHOD';
        const url = err.config?.url || 'UNKNOWN_URL';
        serverLogger.info(
          `Received 403 Forbidden response for ${method} ${url}. Checking JWT token expiration...`,
          {
            method,
            url,
          },
          'api-factory-interceptor',
          getLogOrigin()
        );

        if (isJwtExpired()) {
          serverLogger.warn(
            'JWT token is expired. Logging out the user...',
            {},
            'api-factory-interceptor',
            getLogOrigin()
          );
          logoutUser();
          // TODO: Optionally, we can return a rejected promise or redirect
        } else {
          serverLogger.info(
            'JWT token is valid, but access is forbidden. No action taken.',
            {},
            'api-factory-interceptor',
            getLogOrigin()
          );
        }
      }

      return Promise.reject(err);
    }
  );

  // Return the configured and intercepted Axios instance
  return instance;
};

export default apiFactory;
